if (!customElements.get('key-blanks-showcase')) {
  customElements.define(
    'key-blanks-showcase',
    class KeyBlanksShowcase extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.slides = Array.from(this.querySelectorAll('[data-key-blanks-slide]'));
        if (this.slides.length === 0) return;

        this.initialized = true;
        this.currentIndex = Math.max(
          0,
          this.slides.findIndex((slide) => slide.classList.contains('is-active'))
        );
        this.interval = Number.parseInt(this.dataset.interval, 10) || 5200;
        this.details = this.closest('details');
        this.counter = this.querySelector('[data-key-blanks-counter]');
        this.previousButton = this.querySelector('[data-key-blanks-previous]');
        this.nextButton = this.querySelector('[data-key-blanks-next]');
        this.toggleButton = this.querySelector('[data-key-blanks-toggle]');
        this.progress = this.querySelector('.key-blanks-showcase__progress > span');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.userPaused = this.reducedMotion.matches;
        this.interactionPaused = false;
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.previousButton?.addEventListener('click', () => this.show(this.currentIndex - 1), { signal });
        this.nextButton?.addEventListener('click', () => this.show(this.currentIndex + 1), { signal });
        this.toggleButton?.addEventListener('click', () => this.toggle(), { signal });
        this.addEventListener('pointerenter', () => this.setInteractionPaused(true), { signal });
        this.addEventListener('pointerleave', () => this.setInteractionPaused(false), { signal });
        this.addEventListener(
          'focusin',
          () => this.setInteractionPaused(true),
          { signal }
        );
        this.addEventListener(
          'focusout',
          (event) => {
            if (!this.contains(event.relatedTarget)) this.setInteractionPaused(false);
          },
          { signal }
        );
        this.details?.addEventListener('toggle', () => this.schedule(), { signal });
        document.addEventListener('visibilitychange', () => this.schedule(), { signal });
        this.reducedMotion.addEventListener('change', this.handleMotionPreference, { signal });

        this.updateToggle();
        this.show(this.currentIndex);
      }

      disconnectedCallback() {
        window.clearTimeout(this.timer);
        this.abortController?.abort();
        this.initialized = false;
      }

      handleMotionPreference = (event) => {
        if (event.matches) this.userPaused = true;
        this.updateToggle();
        this.schedule();
      };

      show(index) {
        this.currentIndex = (index + this.slides.length) % this.slides.length;

        this.slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === this.currentIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          if (isActive) {
            slide.removeAttribute('tabindex');
          } else {
            slide.setAttribute('tabindex', '-1');
          }
        });

        if (this.counter) this.counter.textContent = `${this.currentIndex + 1} / ${this.slides.length}`;
        this.schedule();
      }

      toggle() {
        this.userPaused = !this.userPaused;
        this.updateToggle();
        this.schedule();
      }

      updateToggle() {
        if (!this.toggleButton) return;

        const label = this.userPaused
          ? this.toggleButton.dataset.playLabel
          : this.toggleButton.dataset.pauseLabel;
        this.toggleButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
        if (label) this.toggleButton.setAttribute('aria-label', label);
      }

      setInteractionPaused(paused) {
        if (this.interactionPaused === paused) return;
        this.interactionPaused = paused;
        this.schedule();
      }

      canRun() {
        return (
          this.slides.length > 1 &&
          !this.userPaused &&
          !this.interactionPaused &&
          !document.hidden &&
          (!this.details || this.details.open)
        );
      }

      schedule() {
        window.clearTimeout(this.timer);
        this.dataset.running = 'false';

        if (!this.canRun()) return;

        if (this.progress) void this.progress.offsetWidth;
        this.dataset.running = 'true';
        this.timer = window.setTimeout(() => this.show(this.currentIndex + 1), this.interval);
      }
    }
  );
}
